import React, { useState, useEffect, useMemo } from "react"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as predictions from "brandguard/predictions"
import * as authz from "authz"
import classnames from "classnames"
import * as icons from "icons"
import * as debugx from "x/debugx"
import styled from "@emotion/styled"
import { CancellablePromise } from "real-cancellable-promise"
import { Image, Text, Video } from "media"
import * as mediaapi from "media/api"
import { State } from "accounts/api"
import { useSettings } from "accounts/settings"
import { UploadSimple } from "brandguard/components"
import * as bglayouts from "brandguard/components/layouts"
import { styledscroll } from "brandguard/components/layouts"
import * as scores from "brandguard/components/layouts/scores"
import * as nav from "brandguard/components/navigation"
import * as bgapprovals from "brandguard/display/approval"
import { applyOverride, FilterOverride, UnifiedFilterButton, UploadButton } from "brandguard/display/approval/unified.helpers"
import * as bgscores from "brandguard/display/scores"
import * as bgimages from "brandguard/display/brandguard/images"
import * as bgtext from "brandguard/display/brandguard/text"
import * as bgdocuments from "brandguard/display/brandguard/documents"
import * as bgvideos from "brandguard/display/brandguard/videos"
import * as pagecache from "brandguard/display/brandguard/documents/cache"
import * as unified from "brandguard/display/unified"
import * as routing from "brandguard/routing"
import { FeedbackPanel } from "brandguard/feedback"
import { awaitRequest } from "brandguard/display/approval/awaiting"

const OverlayContainer = styled(layouts.containers.absolute)`
	flex-direction: column;
`
OverlayContainer.defaultProps = {
	display: "flex",
	top: "0",
	left: "0",
	width: "100%",
	height: "100%",
}

const PDFContainer = styled(layouts.containers.flex)`
	width: 100%;
	height: 100%;
	align-items: center;
	justify-content: center;
	overflow: auto;
`

const defaultApproved = [api.Prediction.APPROVED, api.Prediction.INREVIEW, api.Prediction.REJECTED]

interface withEvent<T> {
	media?: api.Media
	event?: T
}

function sameMedia<T extends withEvent<unknown>>(a: T | undefined, b: T | undefined): boolean {
	return !!a && !!b && a.media?.id === b.media?.id
}

export default function Display(): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const bearertoken = sessions.useToken()
	const brand = brands.caching.useCached()
	const profile = sessions.useProfile()
	const settings = useSettings()
	const { selected, setSelected, types } = unified.useAssetTypesFilter()

	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState<unified.UnifiedItem | undefined>(undefined)
	const [uploadType, setUploadType] = useState<unified.UploadChoice | undefined>(undefined)
	const [filterFocused, setFilterFocused] = useState(false)
	const [isRevising, setIsRevising] = useState(false)
	const [undoItems, setUndoItems] = useState<unified.UnifiedItem[]>([])
	const undoByMediaId = useMemo(() => {
		const m = new Map<string, unified.UnifiedItem>()
		for (const u of undoItems) if (u.data.media?.id) m.set(u.data.media.id, u)
		return m
	}, [undoItems])
	const [versionHistory, setVersionHistory] = useState<api.ImageSearchResponseItem[]>([])
	const [currentPageIndex, setCurrentPageIndex] = useState(0)
	const [pageImageURL, setPageImageURL] = useState("")
	const [pageImageLoading, setPageImageLoading] = useState(false)

	const overlay = bgimages.overlays.useCached()
	const account = sessions.useAccount()

	const baseline: unified.FanoutRequests = useMemo(
		() => ({
			image: api.images.uploads.searches.zero({
				offset: 0n,
				limit: 50n,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
			text: api.text.uploads.searchZero({
				offset: 0n,
				limit: 50n,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
			document: api.documents.uploads.searches.zero({
				offset: 0n,
				limit: 50n,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
			video: api.videos.uploads.searches.zero({
				offset: 0n,
				limit: 50n,
				brand_id: brand.id,
				queues: [uuid.NIL, brand.id],
				approved: defaultApproved,
			}),
		}),
		[brand.id],
	)

	const [override, setOverride] = useState<Partial<FilterOverride>>({})
	const requests: unified.FanoutRequests = useMemo(
		() => ({
			image: applyOverride(baseline.image, override),
			text: applyOverride(baseline.text, override),
			document: applyOverride(baseline.document, override),
			video: applyOverride(baseline.video, override),
		}),
		[baseline, override],
	)

	const [result, setResult] = useState<unified.FanoutResult>({ items: [] })

	useEffect(() => {
		if (brand.id === uuid.NIL) return
		if (!loading) setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => unified.fanoutSearch(brand.id, types, requests, unified.uploadsSearchers, bearertoken))
			.then((r) => {
				setResult(r)
				setLoading(false)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => {
				console.error("unable to search unified brandguard scoring", cause)
				setLoading(false)
			})
		return p.cancel
	}, [brand.id, types.join(","), requests])

	useEffect(() => {
		if (result.items.length === 0) {
			setSelectedItem(undefined)
			return
		}
		const stillVisible = selectedItem && result.items.some((i) => sameMedia(i.data, selectedItem.data))
		if (!stillVisible) {
			setSelectedItem(result.items[0])
			setCurrentPageIndex(0)
		}
	}, [result])

	useEffect(() => {
		const eventId = selectedItem?.event_type === "image" ? selectedItem.data.event?.id : undefined
		overlay.update(bgimages.overlays.zero({ event_id: eventId }))
	}, [selectedItem?.event_type === "image" ? selectedItem.data.event?.id : undefined])

	useEffect(() => {
		if (selectedItem?.event_type !== "image" || !selectedItem.data.media?.id) {
			setVersionHistory([])
			return
		}
		const p = api.images.uploads
			.versions(selectedItem.data.media.id, brand.id, bearertoken)
			.then((resp) => setVersionHistory(resp.versions))
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => console.warn("unable to fetch version history", cause))
		return p.cancel
	}, [selectedItem?.event_type === "image" ? selectedItem.data.media?.id : undefined])

	const currentDoc = selectedItem?.event_type === "document" ? selectedItem.data : undefined
	const currentPage = currentDoc?.pages?.[currentPageIndex]
	const currentPageId = currentPage?.id

	useEffect(() => {
		if (!currentPageId) {
			setPageImageURL("")
			setPageImageLoading(false)
			return
		}
		setPageImageLoading(true)
		const p = pagecache
			.current(account.id, currentPageId, bearertoken)
			.then((url) => {
				setPageImageURL(url)
				setPageImageLoading(false)
			})
			.catch((cause) => {
				console.error("unable to load document page image", cause)
				setPageImageURL("")
				setPageImageLoading(false)
			})
		return () => {
			if (p && typeof p.cancel === "function") p.cancel()
		}
	}, [currentPageId])

	const replaceItem = (updated: unified.UnifiedItem): void => {
		setResult((prev) => {
			const items = prev.items.map((i) => (sameMedia(i.data, updated.data) ? updated : i))
			return { ...prev, items }
		})
		if (sameMedia(selectedItem?.data, updated.data)) setSelectedItem(updated)
	}

	const removeItems = (ids: string[]): void => {
		setResult((prev) => ({ ...prev, items: prev.items.filter((i) => !ids.includes(i.data.media?.id ?? "")) }))
	}

	const prependItem = (added: unified.UnifiedItem): void => {
		setResult((prev) => ({ ...prev, items: [added, ...prev.items] }))
		setSelectedItem(added)
		setIsRevising(false)
	}

	const agree = async (item: unified.UnifiedItem): Promise<unified.UnifiedItem> => {
		const bearer = authzc.bearer(authzaccount)
		switch (item.event_type) {
			case "image": {
				const res = await api.images.approval.disagree(
					item.data as api.ImageSearchResponseItem & { event: api.ImageEvent },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "image", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
			case "text": {
				const res = await api.text.approval.disagree(
					item.data as api.TextSearchResponseItem & { event: api.TextEvent },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "text", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
			case "document": {
				const res = await api.documents.approval.agree(
					item.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "document", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
			case "video": {
				const res = await api.videos.approval.disagree(
					item.data as api.VideoSearchResponseItem & { event: api.VideoEvent },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "video", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
		}
	}

	const disagree = async (item: unified.UnifiedItem): Promise<unified.UnifiedItem> => {
		const bearer = authzc.bearer(authzaccount)
		const tombstone = { tombstoned_at: new Date().toISOString() }
		const observation = { observation: api.Prediction.DISCARDED_BY_UPLOADER }
		switch (item.event_type) {
			case "image": {
				const data = item.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const res = await api.images.uploads.patch(
					data.event.brand_id,
					data.event.id,
					{ ...data, media: { ...data.media!, ...tombstone }, event: { ...data.event, ...observation } },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "image", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
			case "text": {
				const data = item.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const res = await api.text.uploads.patch(
					data.event.brand_id,
					data.event.id,
					{ ...data, media: { ...data.media!, ...tombstone }, event: { ...data.event, ...observation } },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "text", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
			case "document": {
				const data = item.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
				const res = await api.documents.uploads.patch(
					data.event.brand_id,
					data.event.id,
					{ ...data, media: { ...data.media!, ...tombstone }, event: { ...data.event, ...observation } },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "document", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
			case "video": {
				const data = item.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
				const res = await api.videos.uploads.patch(
					data.event.brand_id,
					data.event.id,
					{ ...data, media: { ...data.media!, ...tombstone }, event: { ...data.event, ...observation } },
					bearer,
				)
				const next: unified.UnifiedItem = { event_type: "video", data: res }
				setUndoItems((u) => [...u, item])
				replaceItem(next)
				return next
			}
		}
	}

	const undo = async (
		current: unified.UnifiedItem,
		previously: unified.UnifiedItem,
	): Promise<unknown> => {
		const bearer = authzc.bearer(authzaccount)
		const clearUndo = () =>
			setUndoItems((u) => u.filter((i) => i.data.media?.id !== previously.data.media?.id))
		switch (previously.event_type) {
			case "image": {
				const prev = previously.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const cur = current.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
				const resp = await api.images.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "image", data: prev })
				return api.images.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.images.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
					.catch(
						httpx.errors.notFound((cause) => {
							console.debug("unable to find approval images", cause)
							return resp
						}),
					)
			}
			case "text": {
				const prev = previously.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const cur = current.data as api.TextSearchResponseItem & { event: api.TextEvent }
				const resp = await api.text.uploads.patch(prev.event.id, prev.event.brand_id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "text", data: prev })
				return api.text.approvallog
					.find(cur.event.id, cur.event.brand_id, bearertoken)
					.then((r) => api.text.approvallog.destroy(r.event!.brand_id, r.event!.id, bearer).then(() => resp))
					.catch(
						httpx.errors.notFound((cause) => {
							console.debug("unable to find approval text", cause)
							return resp
						}),
					)
			}
			case "document": {
				const prev = previously.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
				const resp = await api.documents.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "document", data: prev })
				return resp
			}
			case "video": {
				const prev = previously.data as api.VideoSearchResponseItem & { event: api.VideoEvent }
				const resp = await api.videos.uploads.patch(prev.event.brand_id, prev.event.id, prev, bearer)
				clearUndo()
				replaceItem({ event_type: "video", data: prev })
				return resp
			}
		}
	}

	// unapprove returns an auto approved asset to awaiting a review request.
	const unapprove = (item: unified.UnifiedItem): Promise<unknown> =>
		awaitRequest(item.event_type, item.data, authzc.bearer(authzaccount)).then((data) => {
			setUndoItems((u) => u.filter((i) => i.data.media?.id !== item.data.media?.id))
			replaceItem({ event_type: item.event_type, data } as unified.UnifiedItem)
		})

	const nextItem = () => {
		if (result.items.length === 0 || !selectedItem) return
		const idx = result.items.findIndex((i) => sameMedia(i.data, selectedItem.data))
		const nxt = result.items[(idx + 1) % result.items.length]
		setSelectedItem(nxt)
		setCurrentPageIndex(0)
	}

	const prevItem = () => {
		if (result.items.length === 0 || !selectedItem) return
		const idx = result.items.findIndex((i) => sameMedia(i.data, selectedItem.data))
		const nxt = result.items[(idx - 1 + result.items.length) % result.items.length]
		setSelectedItem(nxt)
		setCurrentPageIndex(0)
	}

	const nextPage = () => {
		if (!currentDoc?.pages) return
		setCurrentPageIndex((p) => Math.min(p + 1, currentDoc.pages!.length - 1))
	}

	const prevPage = () => {
		setCurrentPageIndex((p) => Math.max(p - 1, 0))
	}

	useEffect(() => {
		if (!selectedItem) return
		const handleKeyDown = (evt: KeyboardEvent) => {
			if (evt.key === "ArrowRight") nextItem()
			else if (evt.key === "ArrowLeft") prevItem()
			else if (evt.key === "ArrowDown" && selectedItem.event_type === "document") nextPage()
			else if (evt.key === "ArrowUp" && selectedItem.event_type === "document") prevPage()
		}
		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [selectedItem, currentPageIndex, result.items])

	const uploadDenied = (
		<layouts.containers.flex flex="1" className={classnames(bglayouts.uploads.navdenied)}>
			<authz.layouts.UploadDenied className="styled-area">
				You do not have permissions to upload new assets for scoring. Please contact an admin.
			</authz.layouts.UploadDenied>
		</layouts.containers.flex>
	)

	const MemoUpload = useMemo(() => {
		return function MemodUpload(props: inputs.uploads.api.Props): JSX.Element {
			if (uploadType === undefined || !permission.brandguard_upload) return uploadDenied
			const commonProps = {
				background: layouts.theme.colors.blue.blue,
				observation: api.Prediction.APPROVED,
				...props,
			}
			switch (uploadType) {
				case "image":
					return <bgimages.uploads.default {...commonProps} />
				case "text":
					return <bgtext.uploads.default {...commonProps} />
				case "document":
					return <bgdocuments.uploads.default {...commonProps} />
				case "video":
					return <bgvideos.uploads.default {...commonProps} />
				case "any":
					return <unified.Upload {...commonProps} />
			}
		}
	}, [uploadType, permission.brandguard_upload])

	const uploadPanel = uploadType
		? renderUploadPanel({
				selected: uploadType,
				brandId: brand.id,
				profileId: profile.id,
				bearer: authzc.bearer(authzaccount),
				bearertoken,
				selectedItem,
				isRevising,
				MemoUpload,
				onItemUploaded: prependItem,
				onClose: () => {
					setUploadType(undefined)
					setIsRevising(false)
				},
		  })
		: (<></>)

	return (
		<layouts.containers.flex flexDirection="column" flex="1" mb="25px">
			<layouts.containers.flex flexDirection="column-reverse" flex="1">
				<layouts.loading.pending className="brandguard-working-area" loading={loading} flex="1" overflow="hidden">
					<layouts.containers.flex className="center-panel" flex="1" flexDirection="column">
						{result.items.length === 0 && <bgscores.layouts.PreviewZero />}
						{selectedItem && (
							<layouts.containers.flex
								flex="1"
								flexDirection="row"
								border={layouts.theme.borders.grey.dark50alpha}
								boxShadow={layouts.theme.boxshadow.black.wide}
							>
								<layouts.containers.flex
									className="left-preview-side"
									flex="6"
									borderRight={layouts.theme.borders.grey.dark50alpha}
									alignItems="center"
									justifyContent="center"
									background={layouts.theme.backgrounds.bluealpha5}
									position="relative"
								>
									{renderMedia({
										item: selectedItem,
										overlay,
										currentPageIndex,
										pageImageURL,
										pageImageLoading,
									})}
									{result.items.length > 1 && (
										<>
											<NavArrow left="-20px" onClick={prevItem}>
												<icons.arrows.ArrowLeft />
											</NavArrow>
											<NavArrow right="-20px" onClick={nextItem}>
												<icons.arrows.ArrowRight />
											</NavArrow>
										</>
									)}
									{selectedItem.event_type === "document" &&
										selectedItem.data.pages &&
										selectedItem.data.pages.length > 1 && (
											<>
												<NavArrow top="20px" onClick={prevPage} opacity={currentPageIndex === 0 ? 0.3 : 1}>
													<icons.arrows.ArrowUp />
												</NavArrow>
												<NavArrow
													bottom="20px"
													onClick={nextPage}
													opacity={
														currentPageIndex === selectedItem.data.pages.length - 1 ? 0.3 : 1
													}
												>
													<icons.arrows.ArrowDown />
												</NavArrow>
											</>
										)}
								</layouts.containers.flex>
								<layouts.containers.flex
									flex="5"
									py="10px"
									pl="40px"
									pr="20px"
									className={classnames("right-score-side", bgscores.layouts.styledscores)}
									background={layouts.theme.colors.white}
								>
									<layouts.Flex flex="1" minHeight="0px" height="calc(100vh - 250px)">
										<layouts.Flex flex="1" flexDirection="column" overflowY="auto" pr="10px" className={styledscroll}>
											<bgscores.layouts.accordion.Container initOpen title="Asset Scores">
												{renderScores(selectedItem, currentPage)}
											</bgscores.layouts.accordion.Container>
											{selectedItem.data.event && (
												<FeedbackPanel
													eventType={selectedItem.event_type}
													eventIds={[selectedItem.data.event.id]}
													uploadedBy={selectedItem.data.media?.uploaded_by}
												/>
											)}
											<bgapprovals.status.default>
												{selectedItem.event_type === "video" ? (
													<bgapprovals.status.VideoUploads
														item={selectedItem.data as api.VideoSearchResponseItem & { event: api.VideoEvent }}
														previtemstate={
															undoByMediaId.get(selectedItem.data.media?.id ?? "")?.data as
																| (api.VideoSearchResponseItem & { event: api.VideoEvent })
																| undefined
														}
														agree={(v) => agree({ event_type: "video", data: v }).then((r) => r.data as api.VideoSearchResponseItem)}
														disagree={(v) => disagree({ event_type: "video", data: v }).then((r) => r.data as api.VideoSearchResponseItem)}
														undo={(c, p) =>
															undo(
																{ event_type: "video", data: c },
																{ event_type: "video", data: p },
															) as Promise<api.VideoPatchResponse>
														}
														unapprove={
															permission.brandguard_upload
																? (v) => unapprove({ event_type: "video", data: v })
																: undefined
														}
														borderRadius="10px"
													/>
												) : (
													renderUploadsStatus(selectedItem, undoByMediaId, agree, disagree, undo, permission.brandguard_upload ? unapprove : undefined)
												)}
											</bgapprovals.status.default>
											<bgscores.layouts.accordion.Container title="More Assets">
												<layouts.containers.ResponsiveGrid gap={10} breakpointCols={3} py="10px" px="5px">
													{result.items.map((item) => (
														<MoreAssetCard
															key={item.data.media?.id}
															item={item}
															active={sameMedia(item.data, selectedItem.data)}
															onSelect={() => {
																setSelectedItem(item)
																setCurrentPageIndex(0)
															}}
															onUpdate={replaceItem}
															settingsEnabled={settings.confidence !== State.DISABLED}
														/>
													))}
												</layouts.containers.ResponsiveGrid>
											</bgscores.layouts.accordion.Container>
											<authz.Protected enabled={debugx.alpha.enabled()}>
												<bgscores.layouts.accordion.Container title="Debug Info" textAlign="initial">
													{renderDebug(selectedItem)}
												</bgscores.layouts.accordion.Container>
											</authz.Protected>
											{selectedItem.event_type === "image" && versionHistory.length > 1 && (
												<bgapprovals.VersionHistory
													versions={versionHistory}
													current={selectedItem.data.media?.id || ""}
													onVersionClick={(v) =>
														setSelectedItem({
															event_type: "image",
															data: v as api.ImageSearchResponseItem,
														})
													}
												/>
											)}
											<layouts.containers.flex className="actions"></layouts.containers.flex>
											<authz.Protected enabled={permission.brandguard_upload}>
												{renderMultiActions({
													item: selectedItem,
													isRevising,
													canUpload: permission.brandguard_upload,
													onDelete: (ids) => {
														setSelectedItem(undefined)
														removeItems(ids)
													},
													onRevise: () => {
														if (!selectedItem) return
														setIsRevising(true)
														setUploadType(selectedItem.event_type)
													},
												})}
											</authz.Protected>
										</layouts.Flex>
									</layouts.Flex>
								</layouts.containers.flex>
							</layouts.containers.flex>
						)}
					</layouts.containers.flex>
				</layouts.loading.pending>
				<layouts.containers.flex flexDirection="row" alignItems="center">
					<UploadButton
						onToggle={() => {
							if (filterFocused) setFilterFocused(false)
							setIsRevising(false)
							setUploadType(uploadType ? undefined : unified.anyUpload)
						}}
						panel={uploadPanel}
					/>
					<UnifiedFilterButton
						baseline={baseline.image}
						current={requests.image}
						focused={filterFocused}
						onToggle={() => {
							if (!filterFocused && uploadType) setUploadType(undefined)
							setFilterFocused(!filterFocused)
						}}
						onClear={() => setOverride({})}
						onChange={(next) => setOverride((prev) => ({ ...prev, ...next }))}
						showApprovedRejectedCheckboxes={false}
						assetTypesSelected={selected}
						onAssetTypesChange={setSelected}
					/>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}

function NavArrow(
	props: React.PropsWithChildren<layouts.containers.FlexProps & { onClick: () => void }>,
): JSX.Element {
	const { children, onClick, ...rest } = props
	return (
		<layouts.containers.absolute {...rest}>
			<layouts.Flex
				background={`${layouts.theme.colors.white}BF`}
				boxShadow="0px 1px 2px 0px #00000040"
				alignItems="center"
				justifyContent="center"
				borderRadius="50%"
				width="40px"
				height="40px"
				cursor="pointer"
				onClick={onClick}
			>
				{children}
			</layouts.Flex>
		</layouts.containers.absolute>
	)
}

interface mediaProps {
	item: unified.UnifiedItem
	overlay: bgimages.overlays.cached
	currentPageIndex: number
	pageImageURL: string
	pageImageLoading: boolean
}

function renderMedia(props: mediaProps): JSX.Element {
	const { item, overlay, currentPageIndex, pageImageURL, pageImageLoading } = props
	switch (item.event_type) {
		case "image":
			return (
				<layouts.overlays.Screen enabled>
					<Image media={mediaapi.zero({ content_digest: item.data.media?.md5 })}>
						{overlay.items.map((o, i) => (
							<OverlayContainer key={o.uuid + i} zIndex={o.position}>
								{o.element}
							</OverlayContainer>
						))}
					</Image>
				</layouts.overlays.Screen>
			)
		case "text":
			return (
				<layouts.overlays.Screen
					enabled
					justifyContent="center"
					overflowY="auto"
					className={styledscroll}
				>
					<Text
						media={mediaapi.zero({ content_digest: item.data.media?.md5 })}
						textAlign="left"
						fontWeight="400"
						fontSize="16px"
						lineHeight="30px"
						p="20px"
						my="auto"
					/>
				</layouts.overlays.Screen>
			)
		case "video":
			return (
				<layouts.overlays.Screen enabled>
					<Video
						media={mediaapi.zero({
							content_digest: item.data.media?.md5,
							mimetype: item.data.media?.mimetype,
						})}
						height="100%"
						width="100%"
					/>
				</layouts.overlays.Screen>
			)
		case "document": {
			const page = item.data.pages?.[currentPageIndex]
			return (
				<layouts.overlays.Screen enabled>
					<layouts.loading.screen loading={pageImageLoading} flex="1" height="100%">
						<PDFContainer>
							{page && pageImageURL ? (
								<img
									src={pageImageURL}
									alt={`Page ${currentPageIndex + 1}`}
									style={{
										maxWidth: "100%",
										maxHeight: "100%",
										objectFit: "contain",
										display: "block",
									}}
								/>
							) : (
								<layouts.containers.flex
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									p="20px"
								>
									<icons.brandguard.document width="80px" height="80px" />
									<layouts.Span fontSize="16px" fontWeight="600" mb="10px">
										Page {currentPageIndex + 1} of {item.data.pages?.length || 0}
									</layouts.Span>
									<layouts.Span fontSize="14px" color={layouts.theme.colors.grey.dark50}>
										Document: {item.data.media?.description || item.data.media?.id}
									</layouts.Span>
									{!page && (
										<layouts.Span fontSize="12px" mt="10px" color={layouts.theme.colors.grey.dark50}>
											Loading page...
										</layouts.Span>
									)}
									{page && !pageImageURL && !pageImageLoading && (
										<layouts.Span fontSize="12px" mt="10px" color={layouts.theme.colors.grey.dark50}>
											Page image not available
										</layouts.Span>
									)}
								</layouts.containers.flex>
							)}
						</PDFContainer>
					</layouts.loading.screen>
				</layouts.overlays.Screen>
			)
		}
	}
}

function renderScores(item: unified.UnifiedItem, currentPage: api.DocumentPage | undefined): JSX.Element {
	switch (item.event_type) {
		case "image":
			return <bgimages.Scores item={item.data} />
		case "text":
			return <bgtext.Scores item={item.data} />
		case "document":
			return <bgdocuments.Scores item={item.data} currentPage={currentPage} />
		case "video":
			return <bgvideos.Scores item={item.data} />
	}
}

function renderDebug(item: unified.UnifiedItem): JSX.Element {
	switch (item.event_type) {
		case "image":
			return <bgimages.Debug item={item.data} />
		case "text":
			return <bgtext.Debug item={item.data} />
		case "document":
			return <bgdocuments.Debug item={item.data} />
		case "video":
			return <></>
	}
}

function renderUploadsStatus(
	item: unified.UnifiedItem,
	undoByMediaId: Map<string, unified.UnifiedItem>,
	agree: (i: unified.UnifiedItem) => Promise<unified.UnifiedItem>,
	disagree: (i: unified.UnifiedItem) => Promise<unified.UnifiedItem>,
	undo: (c: unified.UnifiedItem, p: unified.UnifiedItem) => Promise<unknown>,
	unapprove: ((i: unified.UnifiedItem) => Promise<unknown>) | undefined,
): JSX.Element {
	const prev = undoByMediaId.get(item.data.media?.id ?? "")
	switch (item.event_type) {
		case "image": {
			const data = item.data as api.ImageSearchResponseItem & { event: api.ImageEvent }
			return (
				<bgapprovals.status.Uploads
					item={data}
					previtemstate={prev?.data as api.ImageSearchResponseItem | undefined}
					agree={(x) =>
						agree({ event_type: "image", data: x as api.ImageSearchResponseItem }).then(
							(r) => r.data as api.ImageSearchResponseItem,
						)
					}
					disagree={(x) =>
						disagree({ event_type: "image", data: x as api.ImageSearchResponseItem }).then(
							(r) => r.data as api.ImageSearchResponseItem,
						)
					}
					undo={(c, p) =>
						undo(
							{ event_type: "image", data: c as api.ImageSearchResponseItem },
							{ event_type: "image", data: p as api.ImageSearchResponseItem },
						) as Promise<api.ImageSearchResponseItem>
					}
					unapprove={unapprove && ((x) => unapprove({ event_type: "image", data: x as api.ImageSearchResponseItem }))}
					borderRadius="10px"
				/>
			)
		}
		case "text": {
			const data = item.data as api.TextSearchResponseItem & { event: api.TextEvent }
			return (
				<bgapprovals.status.Uploads
					item={data}
					previtemstate={prev?.data as api.TextSearchResponseItem | undefined}
					agree={(x) =>
						agree({ event_type: "text", data: x as api.TextSearchResponseItem }).then(
							(r) => r.data as api.TextSearchResponseItem,
						)
					}
					disagree={(x) =>
						disagree({ event_type: "text", data: x as api.TextSearchResponseItem }).then(
							(r) => r.data as api.TextSearchResponseItem,
						)
					}
					undo={(c, p) =>
						undo(
							{ event_type: "text", data: c as api.TextSearchResponseItem },
							{ event_type: "text", data: p as api.TextSearchResponseItem },
						) as Promise<api.TextSearchResponseItem>
					}
					unapprove={unapprove && ((x) => unapprove({ event_type: "text", data: x as api.TextSearchResponseItem }))}
					borderRadius="10px"
				/>
			)
		}
		case "document": {
			const data = item.data as api.DocumentSearchResponseItem & { event: api.DocumentEvent }
			return (
				<bgapprovals.status.Uploads
					item={data}
					previtemstate={prev?.data as api.DocumentSearchResponseItem | undefined}
					agree={(x) =>
						agree({ event_type: "document", data: x as api.DocumentSearchResponseItem }).then(
							(r) => r.data as api.DocumentSearchResponseItem,
						)
					}
					disagree={(x) =>
						disagree({ event_type: "document", data: x as api.DocumentSearchResponseItem }).then(
							(r) => r.data as api.DocumentSearchResponseItem,
						)
					}
					undo={(c, p) =>
						undo(
							{ event_type: "document", data: c as api.DocumentSearchResponseItem },
							{ event_type: "document", data: p as api.DocumentSearchResponseItem },
						) as Promise<api.DocumentSearchResponseItem>
					}
					unapprove={unapprove && ((x) => unapprove({ event_type: "document", data: x as api.DocumentSearchResponseItem }))}
					borderRadius="10px"
				/>
			)
		}
		case "video":
			return <></>
	}
}

interface moreAssetProps {
	item: unified.UnifiedItem
	active: boolean
	onSelect(): void
	onUpdate(next: unified.UnifiedItem): void
	settingsEnabled: boolean
}

function MoreAssetCard(props: moreAssetProps): JSX.Element {
	const { item, active, onSelect, onUpdate, settingsEnabled } = props
	const panelStyled = (child: JSX.Element) => (
		<authz.Protected enabled={settingsEnabled}>
			<layouts.containers.absolute>{child}</layouts.containers.absolute>
		</authz.Protected>
	)
	switch (item.event_type) {
		case "image":
			return (
				<bgimages.Display
					cardClassName={classnames(bgscores.layouts.styledcardimage)}
					item={item.data}
					active={active}
					onCardClick={onSelect}
					onChange={(v) => onUpdate({ event_type: "image", data: v })}
					panel={panelStyled(<scores.BGScoreImageSimple event={item.data.event!} />)}
					find={api.images.uploads.find}
				/>
			)
		case "text":
			return (
				<bgtext.Display
					className={classnames(bgscores.layouts.styledcardtext)}
					item={item.data}
					active={active}
					onCardClick={onSelect}
					onChange={(v) => onUpdate({ event_type: "text", data: v })}
					panel={panelStyled(<scores.BGScoreTextSimple event={item.data.event!} />)}
					find={api.text.uploads.find}
					border={`2px solid ${layouts.theme.colors.grey.dark50}26`}
				/>
			)
		case "document":
			return (
				<bgdocuments.Display
					cardClassName={classnames(bgscores.layouts.styledcardimage)}
					item={item.data}
					active={active}
					onCardClick={onSelect}
					onChange={(v) => onUpdate({ event_type: "document", data: v })}
					panel={panelStyled(
						<layouts.Flex
							background={layouts.theme.colors.white}
							borderRadius="5px"
							padding="5px 10px"
							fontSize="12px"
							fontWeight="600"
						>
							Score:{" "}
							{item.data.event?.doc_brand_voice !== undefined && item.data.event.doc_brand_voice >= 0
								? `${Math.round(item.data.event.doc_brand_voice * 100)}%`
								: "N/A"}
						</layouts.Flex>,
					)}
					find={api.documents.uploads.find}
				/>
			)
		case "video":
			return (
				<bgvideos.Display
					cardClassName={classnames(bgscores.layouts.styledcardimage)}
					item={item.data}
					active={active}
					onCardClick={onSelect}
					onChange={(v) => onUpdate({ event_type: "video", data: v })}
					panel={panelStyled(<scores.BGScoreVideoSimple event={item.data.event!} />)}
					find={api.videos.uploads.find}
				/>
			)
	}
}

interface multiActionsProps {
	item: unified.UnifiedItem
	isRevising: boolean
	canUpload: boolean
	onDelete(ids: string[]): void
	onRevise(): void
}

function renderMultiActions(props: multiActionsProps): JSX.Element {
	const { item, isRevising, canUpload, onDelete, onRevise } = props
	const revise = (
		<layouts.containers.flex
			className="revise-action"
			title="Revise"
			background={isRevising ? layouts.theme.colors.grey.dark50 + "40" : "transparent"}
			onClick={onRevise}
		>
			<icons.UnstyledFileUpload width="18" height="18" />
		</layouts.containers.flex>
	)
	const collectIds = (results: withEvent<unknown>[]): string[] => results.map((r) => r.media!.id)
	switch (item.event_type) {
		case "image":
			return (
				<bgimages.MultiActions
					uploadItems={[item.data]}
					deleteAction={canUpload ? api.images.uploads.patch : undefined}
					onDelete={(r) => onDelete(collectIds(r))}
				>
					{revise}
				</bgimages.MultiActions>
			)
		case "text":
			return (
				<bgtext.MultiActions
					uploadItems={[item.data]}
					deleteAction={canUpload ? api.text.uploads.patch : undefined}
					onDelete={(r) => onDelete(collectIds(r))}
				>
					{revise}
				</bgtext.MultiActions>
			)
		case "document":
			return (
				<bgdocuments.MultiActions
					uploadItems={[item.data]}
					deleteAction={canUpload ? api.documents.uploads.patch : undefined}
					onDelete={(r) => onDelete(collectIds(r))}
				>
					{revise}
				</bgdocuments.MultiActions>
			)
		case "video":
			return (
				<bgvideos.MultiActions
					uploadItems={[item.data]}
					deleteAction={canUpload ? api.videos.uploads.patch : undefined}
					onDelete={(r) => onDelete(collectIds(r))}
				>
					{revise}
				</bgvideos.MultiActions>
			)
	}
}

interface uploadProps {
	selected: unified.UploadChoice
	brandId: string
	profileId: string
	bearer: httpx.option
	bearertoken: httpx.option
	selectedItem: unified.UnifiedItem | undefined
	isRevising: boolean
	MemoUpload: React.FunctionComponent<React.PropsWithChildren<inputs.uploads.api.Props>>
	onItemUploaded(item: unified.UnifiedItem): void
	onClose(): void
}

function renderUploadPanel(props: uploadProps): JSX.Element {
	const { selected, brandId, profileId, bearer, bearertoken, selectedItem, isRevising, MemoUpload, onItemUploaded, onClose } =
		props
	const shared = {
		className: classnames(nav.layouts.slideDownAnimation),
		DropWell: MemoUpload,
		onClose,
	}
	switch (selected) {
		case "image":
			return (
				<UploadSimple
					{...shared}
					isProcessing={(i: api.ImageSearchResponseItem) => predictions.processing(i.event?.approved)}
					onChange={(img) => onItemUploaded({ event_type: "image", data: img })}
					resume={() => {
						const req = {
							offset: 0n,
							limit: 20n,
							uploaded_by: [profileId],
							brand_id: brandId,
							queues: [uuid.NIL, brandId],
							approved: [api.Prediction.NONE],
						} as api.ImageSearchRequest
						return api.images.uploads.search(req.brand_id, req, bearertoken).then((r) =>
							r.items.map(
								(i) =>
									({
										id: uuid.v4(),
										display: i.media?.description,
										background: layouts.theme.colors.blue.blue,
										data: i,
									} as inputs.uploads.api.uploaded<api.ImageSearchResponseItem>),
							),
						)
					}}
					refresh={(data) =>
						api.images.uploads.find(data.media?.id!, brandId, bearertoken).then((resp) =>
							resp.event?.approved === api.Prediction.ERROR
								? CancellablePromise.reject(resp)
								: CancellablePromise.resolve(resp),
						)
					}
					upload={(_stats, content) =>
						isRevising && selectedItem?.event_type === "image"
							? api.images.uploads.revise(content, brandId, selectedItem.data.media!.id, bearer)
							: api.images.uploads.create(content, brandId, bearer)
					}
				/>
			)
		case "text":
			return (
				<UploadSimple
					{...shared}
					isProcessing={(i: api.TextSearchResponseItem) => predictions.processing(i.event?.approved)}
					onChange={(t) => onItemUploaded({ event_type: "text", data: t })}
					resume={() => {
						const req = {
							offset: 0n,
							limit: 20n,
							uploaded_by: [profileId],
							brand_id: brandId,
							queues: [uuid.NIL, brandId],
							approved: [api.Prediction.NONE],
						} as api.TextSearchRequest
						return api.text.uploads.search(req.brand_id, req, bearertoken).then((r) =>
							r.items.map(
								(i) =>
									({
										id: uuid.v4(),
										display: i.media?.id,
										background: layouts.theme.colors.blue.blue,
										data: i,
									} as inputs.uploads.api.uploaded<api.TextSearchResponseItem>),
							),
						)
					}}
					refresh={(data) =>
						api.text.uploads.find(data.media?.id!, brandId, bearertoken).then((resp) =>
							resp.event?.approved === api.Prediction.ERROR
								? CancellablePromise.reject(resp)
								: CancellablePromise.resolve(resp),
						)
					}
					upload={(_stats, content) =>
						isRevising && selectedItem?.event_type === "text"
							? api.text.uploads.revise(content, brandId, selectedItem.data.media!.id, bearer)
							: api.text.uploads.create(content, brandId, bearer)
					}
				/>
			)
		case "document":
			return (
				<UploadSimple
					{...shared}
					isProcessing={(d: api.DocumentSearchResponseItem) => predictions.processing(d.event?.approved)}
					onChange={(doc) => onItemUploaded({ event_type: "document", data: doc })}
					resume={() => {
						const req = {
							offset: 0n,
							limit: 20n,
							brand_id: brandId,
							queues: [uuid.NIL, brandId],
							approved: [api.Prediction.NONE],
						} as api.DocumentSearchRequest
						return api.documents.uploads.search(req.brand_id, req, bearertoken).then((r) =>
							r.items.map(
								(d) =>
									({
										id: uuid.v4(),
										display: d.media?.description,
										background: layouts.theme.colors.blue.blue,
										data: d,
									} as inputs.uploads.api.uploaded<api.DocumentSearchResponseItem>),
							),
						)
					}}
					refresh={(data) =>
						api.documents.uploads.find(data.media?.id!, brandId, bearertoken).then((resp) =>
							resp.event?.approved === api.Prediction.ERROR
								? CancellablePromise.reject(resp)
								: CancellablePromise.resolve(resp),
						)
					}
					upload={(_stats, content) =>
						isRevising && selectedItem?.event_type === "document"
							? api.documents.uploads.revise(content, brandId, selectedItem.data.media!.id, bearer)
							: api.documents.uploads.create(content, brandId, bearer)
					}
				/>
			)
		case "video":
			return (
				<UploadSimple
					{...shared}
					isProcessing={(i: api.VideoSearchResponseItem) => predictions.processing(i.event?.approved)}
					onChange={(v) => onItemUploaded({ event_type: "video", data: v })}
					resume={() => {
						const req = {
							offset: 0n,
							limit: 20n,
							uploaded_by: [profileId],
							brand_id: brandId,
							queues: [uuid.NIL, brandId],
							approved: [api.Prediction.NONE],
						} as api.VideoSearchRequest
						return api.videos.uploads.search(req.brand_id, req, bearertoken).then((r) =>
							r.items.map(
								(i) =>
									({
										id: uuid.v4(),
										display: i.media?.description,
										background: layouts.theme.colors.blue.blue,
										data: i,
									} as inputs.uploads.api.uploaded<api.VideoSearchResponseItem>),
							),
						)
					}}
					refresh={(data) =>
						api.videos.uploads.find(data.media?.id!, brandId, bearertoken).then((resp) =>
							resp.event?.approved === api.Prediction.ERROR
								? CancellablePromise.reject(resp)
								: CancellablePromise.resolve(resp),
						)
					}
					upload={(_stats, content) =>
						isRevising && selectedItem?.event_type === "video"
							? api.videos.uploads.revise(content, brandId, selectedItem.data.media!.id, bearer)
							: api.videos.uploads.create(content, brandId, bearer)
					}
				/>
			)
		case "any":
			return (
				<UploadSimple
					{...shared}
					isProcessing={(i: unified.UnifiedItem) => predictions.processing(i.data.event?.approved)}
					onChange={onItemUploaded}
					resume={() => unified.resumeUploads(brandId, profileId, bearertoken)}
					refresh={(i) => unified.refreshUpload(i, brandId, bearertoken)}
					upload={(_stats, content) => unified.uploadFile(content, brandId, bearer)}
				/>
			)
	}
}

export function Scoring(): JSX.Element {
	return (
		<bgscores.Container urlgen={routing.score}>
			<layouts.containers.flex flexDirection="column" height="100%" data-testid="brandguard">
				<bgimages.overlays.Storage>
					<Display />
				</bgimages.overlays.Storage>
			</layouts.containers.flex>
		</bgscores.Container>
	)
}
